import { Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { AuthGate } from './AuthGate'
import { AppShell } from '@/components/nav'
import { ToastProvider } from '@/components/ui/ToastProvider'
import Spinner from '@/components/ui/Spinner'
import { useAuth } from '@/lib/auth-context'
import LeafNav from '@/components/nav/LeafNav'
import Footer from '@/components/Footer'
import PublicHeader from '@/components/PublicHeader'

const ImprintPage = lazy(() => import('@/routes/legal/ImprintPage'))
const PrivacyPage = lazy(() => import('@/routes/legal/PrivacyPage'))
const TermsPage = lazy(() => import('@/routes/legal/TermsPage'))
const CommunityPage = lazy(() => import('@/routes/legal/CommunityPage'))
const WithdrawalPage = lazy(() => import('@/routes/legal/WithdrawalPage'))
const AboutPage = lazy(() => import('@/routes/about/AboutPage'))

// ── Public (eager — first paint) ────────────────────────
import { AuthCallback } from './routes/auth/AuthCallback'

// ── Onboarding (eager — small stepped flow) ─────────────
import OnboardingLayout, {
  ManifestoScreen,
  LocationScreen,
  ReaderScreen,
  NameScreen,
  ShareScreen,
  DoneScreen,
} from './routes/onboarding'

// ── Authenticated pages (lazy — code-split) ─────────────
const HomePage = lazy(() => import('./routes/home/HomePage'))
const AddBookPage = lazy(() => import('./routes/books/AddBookPage'))
const MyBooksPage = lazy(() => import('./routes/books/MyBooksPage'))
const TransactionsPage = lazy(() => import('./routes/transactions'))
const DiscoverPage = lazy(() => import('./routes/discover/DiscoverPage'))
const BookDetailPage = lazy(() => import('./routes/discover/BookDetailPage'))
const BookOverviewPage = lazy(() => import('./routes/book/BookOverviewPage'))
const AuthorPage = lazy(() => import('./routes/author/AuthorPage'))
const DossierPage = lazy(() => import('./routes/dossier/DossierPage'))
const RequestsPage = lazy(() => import('./routes/requests/RequestsPage'))
const RequestDetailPage = lazy(() => import('./routes/requests/RequestDetailPage'))
const ChatInboxPage = lazy(() => import('./routes/chat/ChatInboxPage'))
const ChatPage = lazy(() => import('./routes/chat/ChatPage'))
const GroupChatPage = lazy(() => import('./routes/chat/GroupChatPage'))
const VideoFeedPage = lazy(() => import('./routes/views/VideoFeedPage'))
const VideoDetailPage = lazy(() => import('./routes/views/VideoDetailPage'))
const VideoUploadPage = lazy(() => import('./routes/views/VideoUploadPage'))
const VideoAiUploadPage = lazy(() => import('./routes/views/VideoAiUploadPage'))
const ProfilePage = lazy(() => import('./routes/profile/ProfilePage'))
const ProfileHub = lazy(() => import('./routes/profile/ProfileHub'))
const ProfileEdit = lazy(() => import('./routes/profile/ProfileEdit'))
const WritingPage = lazy(() => import('./routes/profile/WritingPage'))
const MyContentPage = lazy(() => import('./routes/profile/MyContentPage'))
const PublicProfilePage = lazy(() => import('./routes/profile/PublicProfilePage'))
const SettingsPage = lazy(() => import('./routes/settings/SettingsPage'))
const NotificationsPage = lazy(() => import('./routes/notifications/NotificationsPage'))
const NewspaperPage = lazy(() => import('./routes/newspaper/NewspaperPage'))
const LoansPage = lazy(() => import('./routes/loans'))
const HandoverPage = lazy(() => import('./routes/handover'))
const WorkWithCherryPage = lazy(() => import('./routes/work-with-cherry/WorkWithCherryPage'))
const PartnerPage = lazy(() => import('./routes/work-with-cherry/PartnerPage'))
const FranchisePage = lazy(() => import('./routes/work-with-cherry/FranchisePage'))
const CareersPage = lazy(() => import('./routes/careers/CareersPage'))
const CareersFlowPage = lazy(() => import('./routes/careers/CareersFlowPage'))
const PartnerOnboarding = lazy(() => import('./routes/partner/PartnerOnboarding'))
const PartnerSuccess = lazy(() => import('./routes/partner/PartnerSuccess'))
const PartnerDashboard = lazy(() => import('./routes/partner/PartnerDashboard'))
const ReaderPage = lazy(() => import('./routes/reader/ReaderPage'))
const GiftClaimPage = lazy(() => import('./routes/gift/GiftClaimPage'))
const LoginGatePage = lazy(() => import('./routes/auth/LoginGatePage'))
const ViewsLandingPage = lazy(() => import('./routes/views/ViewsLandingPage'))
const PublicLandingPage = lazy(() => import('./routes/landing/PublicLandingPage'))

function PageLoader() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Spinner size="lg" />
    </div>
  )
}

/**
 * Newspaper is publicly reachable (cherry-times.com rewrites "/" → "/newspaper").
 * Logged-in visitors get the full app chrome (LeafNav drawer); anonymous visitors
 * get a clean public shell — parchment + footer, no app navigation that would lead
 * them into login walls. Either way the page itself renders for everyone.
 */
function PublicNewspaperRoute() {
  const { user, loading } = useAuth()
  if (loading) return <PageLoader />
  return (
    <div className="flex min-h-screen flex-col bg-parchment">
      {user ? <LeafNav /> : <PublicHeader />}
      <main className="flex-1">
        <Suspense fallback={<PageLoader />}>
          <NewspaperPage />
        </Suspense>
      </main>
      <Footer />
    </div>
  )
}

/* cherry-times.com is the public "newspaper" face of Cherry. When the app is
   loaded on that host (apex or www), the root path shows the newspaper instead
   of the sign-in page. Done in-app (not via a Netlify host-rewrite) so it's
   deterministic and not subject to SPA redirect shadowing. cherry-books.com and
   every other host keep the normal sign-in at "/". */
const TIMES_HOSTS = ['cherry-times.com', 'www.cherry-times.com']
function isTimesHost(): boolean {
  if (typeof window === 'undefined') return false
  return TIMES_HOSTS.includes(window.location.hostname)
}

/* cherry-views.com is the public landing face of Cherry Views — same pattern
   as cherry-times.com above: the root path shows a quiet landing page instead
   of the sign-in page when the app is loaded on that host (apex or www). */
const VIEWS_HOSTS = ['cherry-views.com', 'www.cherry-views.com']
function isViewsHost(): boolean {
  if (typeof window === 'undefined') return false
  return VIEWS_HOSTS.includes(window.location.hostname)
}

/**
 * Reader page — public. Works for both anonymous visitors (public texts only)
 * and logged-in users (who may see selected/on_request texts or buy paid ones).
 */
function PublicReaderRoute() {
  const { user, loading } = useAuth()
  if (loading) return <PageLoader />
  return (
    <div className="flex min-h-screen flex-col bg-parchment">
      {user ? <LeafNav /> : <PublicHeader />}
      <main className="flex-1">
        <Suspense fallback={<PageLoader />}>
          <ReaderPage />
        </Suspense>
      </main>
      <Footer />
    </div>
  )
}

/**
 * Cherry Views — discovery feed and video detail are public (visible
 * logged-out too). Logged-in visitors get the full app chrome; anonymous
 * visitors get a clean public shell. Upload stays behind the AuthGate.
 */
function PublicViewsRoute({ page }: { page: 'feed' | 'detail' }) {
  const { user, loading } = useAuth()
  if (loading) return <PageLoader />
  return (
    <div className="flex min-h-screen flex-col bg-parchment">
      {user ? <LeafNav /> : <PublicHeader />}
      <main className="flex-1">
        <Suspense fallback={<PageLoader />}>
          {page === 'feed' ? <VideoFeedPage /> : <VideoDetailPage />}
        </Suspense>
      </main>
      <Footer />
    </div>
  )
}

/**
 * cherry-views.com landing — public. Anonymous visitors get the quiet public
 * shell (PublicHeader); logged-in visitors get the LeafNav.
 */
function PublicViewsLandingRoute() {
  const { user, loading } = useAuth()
  if (loading) return <PageLoader />
  return (
    <div className="flex min-h-screen flex-col bg-parchment">
      {user ? <LeafNav /> : <PublicHeader />}
      <main className="flex-1">
        <Suspense fallback={<PageLoader />}>
          <ViewsLandingPage />
        </Suspense>
      </main>
      <Footer />
    </div>
  )
}

/**
 * Generic public chrome for info pages (About, work-with-cherry funnel):
 * LeafNav for logged-in visitors, PublicHeader for anonymous ones, Footer below.
 */
function PublicChromeRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return <PageLoader />
  return (
    <div className="flex min-h-screen flex-col bg-parchment">
      {user ? <LeafNav /> : <PublicHeader />}
      <main className="flex-1">
        <Suspense fallback={<PageLoader />}>{children}</Suspense>
      </main>
      <Footer />
    </div>
  )
}

/* Legal pages render their own Footer (in LegalPage) — only the header/nav differs. */
function LegalRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()
  if (loading) return <PageLoader />
  return (
    <div className="flex min-h-screen flex-col bg-parchment">
      {user ? <LeafNav /> : <PublicHeader />}
      <main className="flex-1">
        <Suspense fallback={<PageLoader />}>{children}</Suspense>
      </main>
    </div>
  )
}

/**
 * cherry-books.com landing — public. Anonymous visitors get the quiet public
 * shell (PublicHeader) with the landing page (hero + magic-link card, tiles
 * to the sibling publications, Work-with-Cherry invitation); logged-in
 * visitors get the LeafNav and a "continue" button instead of the form.
 */
function PublicLandingRoute() {
  const { user, loading } = useAuth()
  if (loading) return <PageLoader />
  return (
    <div className="flex min-h-screen flex-col bg-parchment">
      {user ? <LeafNav /> : <PublicHeader />}
      <main className="flex-1">
        <Suspense fallback={<PageLoader />}>
          <PublicLandingPage />
        </Suspense>
      </main>
      <Footer />
    </div>
  )
}

function RootRoute() {
  if (isViewsHost()) return <PublicViewsLandingRoute />
  if (isTimesHost()) return <PublicNewspaperRoute />
  return <PublicLandingRoute />
}

function App() {
  return (
    <ToastProvider>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Root — newspaper on cherry-times.com, views landing on cherry-views.com, public landing everywhere else */}
          <Route path="/" element={<RootRoute />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/login-gate" element={<LoginGatePage />} />

          {/* Newspaper — public (also the target of the cherry-times.com rewrite) */}
          <Route path="/newspaper" element={<PublicNewspaperRoute />} />

          {/* Legal — public (Impressum & Datenschutz must be reachable without login) */}
          <Route path="/impressum" element={<LegalRoute><ImprintPage /></LegalRoute>} />
          <Route path="/datenschutz" element={<LegalRoute><PrivacyPage /></LegalRoute>} />
          <Route path="/agb" element={<LegalRoute><TermsPage /></LegalRoute>} />
          <Route path="/community" element={<LegalRoute><CommunityPage /></LegalRoute>} />
          <Route path="/widerruf" element={<LegalRoute><WithdrawalPage /></LegalRoute>} />

          {/* About — public */}
          <Route path="/about" element={<PublicChromeRoute><AboutPage /></PublicChromeRoute>} />

          {/* Careers — public (quiet invitation, no open positions) */}
          <Route path="/careers" element={<PublicChromeRoute><CareersPage /></PublicChromeRoute>} />
          <Route path="/careers/flow" element={<PublicChromeRoute><CareersFlowPage /></PublicChromeRoute>} />

          {/* Work with Cherry — public funnel (application steps guard themselves) */}
          {/* Short URLs — keep before the catch-all so they don't redirect to "/" */}
          <Route path="/partner" element={<Navigate to="/work-with-cherry/partner" replace />} />
          <Route path="/franchise" element={<Navigate to="/work-with-cherry/franchise" replace />} />

          <Route path="/work-with-cherry" element={<PublicChromeRoute><WorkWithCherryPage /></PublicChromeRoute>} />
          <Route path="/work-with-cherry/partner" element={<PublicChromeRoute><PartnerPage /></PublicChromeRoute>} />
          <Route path="/work-with-cherry/franchise" element={<PublicChromeRoute><FranchisePage /></PublicChromeRoute>} />
          <Route path="/partner/onboarding" element={<PublicChromeRoute><PartnerOnboarding /></PublicChromeRoute>} />
          <Route path="/partner/success" element={<PublicChromeRoute><PartnerSuccess /></PublicChromeRoute>} />

          {/* Reader — public (any text, visibility + paywall handled inside) */}
          <Route path="/read/:textId" element={<PublicReaderRoute />} />

          {/* Cherry Views — public feed + detail (upload is authenticated below) */}
          <Route path="/views" element={<PublicViewsRoute page="feed" />} />
          <Route path="/views/:id" element={<PublicViewsRoute page="detail" />} />

          {/* Dossier — product page (buy / download), public chrome like Reader */}
          <Route path="/dossier/:textId" element={<PublicChromeRoute><DossierPage /></PublicChromeRoute>} />

          {/* Onboarding — no shell, stepped flow */}
          <Route path="/onboarding" element={<Navigate to="/onboarding/manifesto" replace />} />
          <Route element={<OnboardingLayout />}>
            <Route path="/onboarding/manifesto" element={<ManifestoScreen />} />
            <Route path="/onboarding/location" element={<LocationScreen />} />
            <Route path="/onboarding/reader" element={<ReaderScreen />} />
            <Route path="/onboarding/name" element={<NameScreen />} />
            <Route path="/onboarding/share" element={<ShareScreen />} />
            <Route path="/onboarding/done" element={<DoneScreen />} />
          </Route>

          {/* Authenticated — wrapped in AppShell */}
          <Route
            element={
              <AuthGate>
                <AppShell />
              </AuthGate>
            }
          >
            <Route path="/home" element={<HomePage />} />
            <Route path="/discover" element={<DiscoverPage />} />
            <Route path="/discover/:cbcId" element={<BookDetailPage />} />
            <Route path="/book/:bookId" element={<BookOverviewPage />} />
            <Route path="/author/:nameKey" element={<AuthorPage />} />
            <Route path="/books/add" element={<AddBookPage />} />
            <Route path="/books/mine" element={<MyBooksPage />} />
            <Route path="/transactions" element={<TransactionsPage />} />
            <Route path="/requests" element={<RequestsPage />} />
            <Route path="/requests/:requestId" element={<RequestDetailPage />} />
            <Route path="/chat" element={<ChatInboxPage />} />
            <Route path="/chat/group/:conversationId" element={<GroupChatPage />} />
            <Route path="/chat/:userId" element={<ChatPage />} />
            <Route path="/views/upload" element={<VideoUploadPage />} />
            {/* Cherry AI — Erste Montage (statischer Pfad, schattet /views/upload nicht) */}
            <Route path="/views/upload/ai" element={<VideoAiUploadPage />} />
            <Route path="/handover/:requestId" element={<HandoverPage />} />
            <Route path="/loans" element={<LoansPage />} />
            <Route path="/gift/:token" element={<GiftClaimPage />} />
            <Route path="/profile" element={<ProfileHub />} />
            <Route path="/profile/edit" element={<ProfileEdit />} />
            <Route path="/profile/writing" element={<WritingPage />} />
            <Route path="/profile/content" element={<MyContentPage />} />
            <Route path="/u/:userId" element={<PublicProfilePage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/partner/dashboard" element={<PartnerDashboard />} />
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </ToastProvider>
  )
}

export default App
